import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const Section = styled.section`
  width: 100%;
  min-height: 100vh;
  background: #000;
  color: #fff;
  padding: 120px 20px 80px;
  display: flex;
  justify-content: center;
`;

const Container = styled.div`
  max-width: 800px;
  width: 100%;
  font-family: system-ui, -apple-system, sans-serif;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 6vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 2rem;
  text-align: center;
  background: linear-gradient(120deg, #fff, #9ca3af);
  background-size: 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradientMove 8s ease-in-out infinite alternate;

  @keyframes gradientMove {
    0% { background-position: 0%; }
    100% { background-position: 100%; }
  }
`;

const Subtitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 2.5rem 0 1rem;
  color: #e5e5e5;
`;

const Text = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  opacity: 0.85;
  margin-bottom: 1.2rem;
  color: #ccc;
`;

const List = styled.ul`
  padding-left: 1.5rem;
  margin: 1rem 0;
  opacity: 0.9;
`;

const ListItem = styled.li`
  margin-bottom: 0.6rem;
  font-size: 1.05rem;
  line-height: 1.6;
`;

const ContactLink = styled.a`
  color: #60a5fa;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const PrivacyPolicy = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <Section>
        <Container>
          <Title>Privacy Policy</Title>
          <Text>Last updated: November 15, 2025</Text>

          <Text>
            At <strong>Plano AI</strong>, we respect your privacy and are committed to protecting your personal data. 
            This Privacy Policy explains how we collect, use, and safeguard your information when you use our mobile application.
          </Text>

          <Subtitle>1. Information We Collect</Subtitle>
          <Text>We collect the following types of information:</Text>
          <List>
            <ListItem><strong>Personal Information:</strong> Name, email address (when you sign up or contact us).</ListItem>
            <ListItem><strong>Usage Data:</strong> Goals, tasks, progress, app interactions.</ListItem>
            <ListItem><strong>Device Info:</strong> Device type, OS version, unique device identifiers (for analytics and improvements).</ListItem>
            <ListItem><strong>Analytics:</strong> Anonymous usage patterns via Firebase Analytics and Mixpanel.</ListItem>
          </List>

          <Subtitle>2. How We Use Your Information</Subtitle>
          <Text>Your data helps us:</Text>
          <List>
            <ListItem>Provide and improve AI-powered planning features</ListItem>
            <ListItem>Send progress summaries and motivational insights</ListItem>
            <ListItem>Fix bugs and enhance app performance</ListItem>
            <ListItem>Offer personalized recommendations</ListItem>
          </List>

          <Subtitle>3. Data Storage & Security</Subtitle>
          <Text>
            Your data is stored securely using encrypted cloud services (AWS, Firebase). 
            We use industry-standard security measures including SSL encryption and regular security audits.
          </Text>

          <Subtitle>4. Third-Party Services</Subtitle>
          <Text>We use the following trusted partners:</Text>
          <List>
            <ListItem><strong>Google Analytics / Firebase</strong> – app performance</ListItem>
            <ListItem><strong>OpenAI / Grok API</strong> – AI goal analysis (anonymized input only)</ListItem>
            <ListItem><strong>Stripe</strong> – secure payment processing (no card data stored)</ListItem>
          </List>

          <Subtitle>5. Your Rights</Subtitle>
          <Text>You have the right to:</Text>
          <List>
            <ListItem>Access, update, or delete your personal data</ListItem>
            <ListItem>Opt out of marketing emails</ListItem>
            <ListItem>Request data export or deletion</ListItem>
          </List>
          <Text>
            To exercise these rights, email us at <ContactLink href="mailto:fluencboy@gmail.com">fluencboy@gmail.com</ContactLink>
          </Text>

          <Subtitle>6. Children's Privacy</Subtitle>
          <Text>
            Plano AI is not intended for children under 13. We do not knowingly collect data from children.
          </Text>

          <Subtitle>7. Changes to This Policy</Subtitle>
          <Text>
            We may update this policy from time to time. We will notify you via email or in-app notification.
          </Text>

          <Subtitle>8. Contact Us</Subtitle>
          <Text>
            Questions? Reach out:<br />
            Email: <ContactLink href="mailto:fluencboy@gmail.com">fluencboy@gmail.com</ContactLink><br />
            Company: Plano AI Inc.<br />
            Address: 548 Market St, San Francisco, CA 94104, USA
          </Text>

          <Text style={{ marginTop: '3rem', fontSize: '0.9rem', opacity: 0.6, textAlign: 'center' }}>
            © 2025 Plano AI. All rights reserved.
          </Text>
        </Container>
      </Section>
    </motion.div>
  );
};

export default PrivacyPolicy;